import {
  type DocumentLine,
  LineType,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { SHOW_TEXT_HINTS_IN_GROUPED_VIEW } from './constants';

export interface DeliveryBucket {
  deliveryNumber: string | null;
  orderNumber: string | null;
  deliveryDate: string | null;
  orderDate: string | null;
  commission: string | null;
  /** ARTICLE lines only — used for selection, position counts and sums. */
  lines: DocumentLine[];
  /**
   * Lines to render in order: articles plus interleaved TEXT hint lines
   * (when SHOW_TEXT_HINTS_IN_GROUPED_VIEW is enabled). Kept separate from
   * `lines` so selection/count logic stays article-only.
   */
  displayLines: DocumentLine[];
  sum: number;
}

const DELIVERY_RE = /Lieferungsnr\.\s*:\s*(VL\d+)(?:.*?Lieferdatum\s*:\s*(\d{2}\.\d{2}\.\d{2,4}))?/i;
const ORDER_RE = /Auftragsnr\.\s*:\s*(AU\d+)(?:.*?Auftragsdatum\s*:\s*(\d{2}\.\d{2}\.\d{2,4}))?/i;
const COMMISSION_RE = /(?:BV|Kommission)\s*:\s*(.+)$/i;

/**
 * Group ARTICLE lines by their deliveryNumber. TEXT lines with delivery /
 * order references are consumed and their date metadata attached to the
 * matching bucket — they are not rendered separately. Lines without a
 * deliveryNumber land in a single "default" bucket whose header is hidden
 * (Einzelrechnung look).
 */
export function groupLinesByDelivery(lines: DocumentLine[]): DeliveryBucket[] {
  const buckets = new Map<string, DeliveryBucket>();
  const ensureBucket = (key: string, partial: Partial<DeliveryBucket>) => {
    let b = buckets.get(key);
    if (!b) {
      b = {
        deliveryNumber: null,
        orderNumber: null,
        deliveryDate: null,
        orderDate: null,
        commission: null,
        lines: [],
        displayLines: [],
        sum: 0,
        ...partial,
      };
      buckets.set(key, b);
    } else {
      Object.assign(b, partial, {
        deliveryNumber: b.deliveryNumber ?? partial.deliveryNumber ?? null,
        orderNumber: b.orderNumber ?? partial.orderNumber ?? null,
        deliveryDate: b.deliveryDate ?? partial.deliveryDate ?? null,
        orderDate: b.orderDate ?? partial.orderDate ?? null,
        commission: b.commission ?? partial.commission ?? null,
      });
    }
    return b;
  };

  // Tracks the bucket most recently touched so that non-marker TEXT hints
  // (e.g. "Bruchersatz:") attach to the delivery they visually belong to.
  let currentKey: string | null = null;

  for (const line of lines) {
    if (line.lineType === LineType.ARTICLE) {
      const key = line.deliveryNumber ?? '__default__';
      const bucket = ensureBucket(key, {
        deliveryNumber: line.deliveryNumber ?? null,
        orderNumber: line.orderNumber ?? null,
      });
      bucket.lines.push(line);
      bucket.displayLines.push(line);
      bucket.sum += line.amount ?? 0;
      currentKey = key;
      continue;
    }

    if (line.lineType === LineType.TEXT) {
      const text = `${line.description ?? ''} ${line.description2 ?? ''}`.trim();
      if (!text) continue;
      const dm = text.match(DELIVERY_RE);
      const om = text.match(ORDER_RE);
      const cm = text.match(COMMISSION_RE);

      if (dm) {
        const key = dm[1];
        ensureBucket(key, {
          deliveryNumber: dm[1],
          deliveryDate: dm[2] ?? null,
        });
        currentKey = key;
      }
      if (om) {
        // The order marker positionally belongs to the current delivery block,
        // so attach it (incl. its Auftragsdatum) to the current bucket. Fall
        // back to a bucket already referencing this order, then to a delivery
        // matched on the same line.
        let key: string | null = currentKey;
        if (!key) {
          buckets.forEach((b, k) => {
            if (b.orderNumber === om[1] && !key) key = k;
          });
        }
        if (!key && dm) key = dm[1];
        if (key) {
          ensureBucket(key, {
            orderNumber: om[1],
            orderDate: om[2] ?? null,
          });
        }
      }
      if (cm) {
        const lastKey = Array.from(buckets.keys()).pop();
        if (lastKey) {
          ensureBucket(lastKey, { commission: cm[1].trim() });
        }
      }

      // Non-marker hint text (e.g. "Bruchersatz:") — render it inside the
      // delivery it belongs to (gated by the feature flag).
      if (SHOW_TEXT_HINTS_IN_GROUPED_VIEW && !dm && !om && !cm) {
        const key = currentKey ?? '__default__';
        ensureBucket(key, {}).displayLines.push(line);
      }
    }
  }

  return Array.from(buckets.values());
}

/**
 * A render item: an article line, or a block of consecutive TEXT hint lines.
 */
export type GroupedLineItem =
  | { kind: 'article'; line: DocumentLine }
  | { kind: 'text-group'; key: string; texts: string[] };

/**
 * Collapse a line list into render items: ARTICLE lines pass through, and
 * consecutive TEXT lines fold into one hint block. SEPARATOR lines are dropped.
 * Shared by the flat document views and the grouped delivery cards.
 */
export function groupLineItems(lines: DocumentLine[]): GroupedLineItem[] {
  const items: GroupedLineItem[] = [];
  let group: { key: string; texts: string[] } | null = null;
  const flush = () => {
    if (group && group.texts.length > 0) {
      items.push({ kind: 'text-group', key: group.key, texts: group.texts });
    }
    group = null;
  };
  for (const line of lines) {
    if (line.lineType === LineType.TEXT) {
      if (!group) group = { key: `text-${line.lineNumber}`, texts: [] };
      if (line.description) group.texts.push(line.description);
    } else if (line.lineType === LineType.ARTICLE) {
      flush();
      items.push({ kind: 'article', line });
    }
    // SEPARATOR lines are intentionally skipped.
  }
  flush();
  return items;
}
